// Confidence Metadata Utilities
// Task #10: Helper functions for working with enhanced confidence data models

import {
  PayrollCase,
  ConfidenceMetadata,
  ProcessingMetadata,
  CaseRoutingInfo,
  QualityAssuranceInfo,
  ConfidenceHistoryEntry,
  getConfidenceLevel,
} from '../types';

// Utility to initialize default confidence metadata for existing cases
export const initializeConfidenceMetadata = (
  caseItem: PayrollCase
): ConfidenceMetadata => {
  const level = getConfidenceLevel(caseItem.aiConfidence);
  const timestamp = new Date().toISOString();

  return {
    originalConfidence: caseItem.aiConfidence,
    adjustedConfidence: caseItem.aiConfidence,
    confidenceLevel: level,
    detailedLevel:
      caseItem.aiConfidence >= 95
        ? 'auto_process'
        : caseItem.aiConfidence >= 90
          ? 'high'
          : caseItem.aiConfidence >= 75
            ? 'medium'
            : caseItem.aiConfidence >= 60
              ? 'low'
              : 'critical',
    confidenceHistory: [
      {
        timestamp,
        confidence: caseItem.aiConfidence,
        level,
        trigger: 'initial',
        adjustmentReason: 'Initial case confidence from AI processing',
      },
    ],
    lastEvaluationTimestamp: timestamp,
    evaluationCount: 1,
  };
};

// Utility to initialize default processing metadata
export const initializeProcessingMetadata = (
  caseItem: PayrollCase
): ProcessingMetadata => {
  return {
    processingAttempts: 1,
    lastProcessingAttempt: new Date().toISOString(),
    processingErrors: [],
    businessRuleViolations: [],
    dataQualityScore: caseItem.aiConfidence / 100, // Convert percentage to decimal
    documentComplexity: 0.5, // Default medium complexity
    extractionMethod: 'HYBRID', // Default assumption
    processingDuration: 0, // Will be updated during actual processing
  };
};

// Utility to initialize default routing info
export const initializeRoutingInfo = (caseItem: PayrollCase): CaseRoutingInfo => {
  const confidence = caseItem.aiConfidence;
  let workflow: CaseRoutingInfo['assignedWorkflow'];
  let estimatedTime: string;
  let requiresCSR: boolean;

  if (confidence >= 95) {
    workflow = 'AUTO_PROCESS';
    estimatedTime = '2_minutes';
    requiresCSR = false;
  } else if (confidence >= 90) {
    workflow = 'HIGH_CONFIDENCE';
    estimatedTime = '5_minutes';
    requiresCSR = true;
  } else if (confidence >= 75) {
    workflow = 'MEDIUM_CONFIDENCE';
    estimatedTime = '15_minutes';
    requiresCSR = true;
  } else if (confidence >= 60) {
    workflow = 'LOW_CONFIDENCE';
    estimatedTime = '30_minutes';
    requiresCSR = true;
  } else {
    workflow = 'MANUAL_ESCALATION';
    estimatedTime = 'indefinite';
    requiresCSR = false;
  }

  return {
    assignedWorkflow: workflow,
    estimatedProcessingTime: estimatedTime,
    requiresCSRReview: requiresCSR,
    requiresFlagging: confidence < 85,
    flaggingReason: confidence < 85 ? 'Below visibility threshold' : undefined,
    escalationLevel: confidence < 60 ? 'supervisor' : 'none',
    routingTimestamp: new Date().toISOString(),
    workflowStatus: 'pending',
  };
};

// Utility to initialize default QA info
export const initializeQualityAssurance = (
  caseItem: PayrollCase
): QualityAssuranceInfo => {
  const confidence = caseItem.aiConfidence;

  return {
    qaCheckpoints: [],
    overallQAStatus: 'pending',
    finalValidationRequired: confidence < 90,
    supervisorApprovalRequired: confidence < 75,
    supervisorApprovalStatus: confidence < 75 ? 'pending' : undefined,
  };
};

// Utility to enhance an existing PayrollCase with full metadata
export const enhancePayrollCaseWithMetadata = (caseItem: PayrollCase): PayrollCase => {
  // Only add metadata if it doesn't already exist
  return {
    ...caseItem,
    confidenceMetadata:
      caseItem.confidenceMetadata || initializeConfidenceMetadata(caseItem),
    processingMetadata:
      caseItem.processingMetadata || initializeProcessingMetadata(caseItem),
    routingInfo: caseItem.routingInfo || initializeRoutingInfo(caseItem),
    qualityAssurance: caseItem.qualityAssurance || initializeQualityAssurance(caseItem),
  };
};

// Utility to enhance an array of cases
export const enhancePayrollCasesWithMetadata = (
  cases: PayrollCase[]
): PayrollCase[] => {
  return cases.map(enhancePayrollCaseWithMetadata);
};

// Utility to check if a case has full metadata
export const hasFullMetadata = (caseItem: PayrollCase): boolean => {
  return !!(
    caseItem.confidenceMetadata &&
    caseItem.processingMetadata &&
    caseItem.routingInfo &&
    caseItem.qualityAssurance
  );
};

// Utility to get confidence metadata with fallback
export const getConfidenceMetadata = (caseItem: PayrollCase): ConfidenceMetadata => {
  return caseItem.confidenceMetadata || initializeConfidenceMetadata(caseItem);
};

// Utility to get processing metadata with fallback
export const getProcessingMetadata = (caseItem: PayrollCase): ProcessingMetadata => {
  return caseItem.processingMetadata || initializeProcessingMetadata(caseItem);
};

// Utility to get routing info with fallback
export const getRoutingInfo = (caseItem: PayrollCase): CaseRoutingInfo => {
  return caseItem.routingInfo || initializeRoutingInfo(caseItem);
};

// Utility to get QA info with fallback
export const getQualityAssurance = (caseItem: PayrollCase): QualityAssuranceInfo => {
  return caseItem.qualityAssurance || initializeQualityAssurance(caseItem);
};

// Utility to add a confidence history entry
export const addConfidenceHistoryEntry = (
  caseItem: PayrollCase,
  newConfidence: number,
  trigger: ConfidenceHistoryEntry['trigger'],
  reason?: string,
  adjustmentAmount?: number
): PayrollCase => {
  const metadata = getConfidenceMetadata(caseItem);
  const newLevel = getConfidenceLevel(newConfidence);

  const newEntry: ConfidenceHistoryEntry = {
    timestamp: new Date().toISOString(),
    confidence: newConfidence,
    level: newLevel,
    trigger,
    adjustmentReason: reason,
    adjustmentAmount,
  };

  return {
    ...caseItem,
    aiConfidence: newConfidence,
    confidenceMetadata: {
      ...metadata,
      adjustedConfidence: newConfidence,
      confidenceLevel: newLevel,
      detailedLevel:
        newConfidence >= 95
          ? 'auto_process'
          : newConfidence >= 90
            ? 'high'
            : newConfidence >= 75
              ? 'medium'
              : newConfidence >= 60
                ? 'low'
                : 'critical',
      confidenceHistory: [...metadata.confidenceHistory, newEntry],
      lastEvaluationTimestamp: newEntry.timestamp,
      evaluationCount: metadata.evaluationCount + 1,
    },
  };
};

// Utility to check if case meets visibility requirements
export const meetsVisibilityRequirements = (caseItem: PayrollCase): boolean => {
  const routingInfo = getRoutingInfo(caseItem);
  return !routingInfo.requiresFlagging && caseItem.aiConfidence >= 85;
};

// Utility to get workflow summary for display
export const getWorkflowSummary = (
  caseItem: PayrollCase
): {
  workflow: string;
  timeEstimate: string;
  requiresReview: boolean;
  status: string;
} => {
  const routing = getRoutingInfo(caseItem);

  const workflowNames = {
    AUTO_PROCESS: 'Automated Processing',
    HIGH_CONFIDENCE: 'Quick Review',
    MEDIUM_CONFIDENCE: 'Standard Review',
    LOW_CONFIDENCE: 'Enhanced Review',
    MANUAL_ESCALATION: 'Manual Escalation',
  };

  return {
    workflow: workflowNames[routing.assignedWorkflow] || routing.assignedWorkflow,
    timeEstimate: routing.estimatedProcessingTime.replace('_', ' '),
    requiresReview: routing.requiresCSRReview,
    status: routing.workflowStatus,
  };
};
